import { useCallback } from 'react'
import { feedUrl, loginUrl } from '@/urls'

const isAuthPath = path => typeof path === 'string' && (path === loginUrl() || path.startsWith('/l/'))

/**
 * Centralized hook for managing lastPath logic
 * Handles setting and retrieving the last path before login redirect
 */
const useLastPath = () => {
  /**
   * Sets the current path as the last path before redirecting to login
   * Only sets if the current path is not an auth (/l/) route
   */
  const setLastPath = useCallback((currentPath = typeof window !== 'undefined' ? window.location.pathname + (window.location.search || '') : '') => {
    if (typeof window === 'undefined') return
    if (!isAuthPath(currentPath)) {
      localStorage.setItem('lastPath', currentPath)
    }
  }, [])

  /**
   * Gets the last path from localStorage, with fallback to feed
   * Cleans up the lastPath if it's an auth route
   */
  const getLastPath = useCallback(() => {
    if (typeof window === 'undefined') return feedUrl()
    const lastPath = localStorage.getItem('lastPath') || feedUrl()
    if (isAuthPath(lastPath)) {
      localStorage.removeItem('lastPath')
      return feedUrl()
    }
    return lastPath
  }, [])

  /**
   * Clears the lastPath from localStorage
   */
  const clearLastPath = useCallback(() => {
    if (typeof window === 'undefined') return
    localStorage.removeItem('lastPath')
  }, [])

  /**
   * Navigates to login and sets current path as lastPath + returnUrl state
   */
  const redirectToLogin = useCallback(
    (
      navigate,
      currentPath =
        typeof window !== 'undefined' ? window.location.pathname + (window.location.search || '') : feedUrl()
    ) => {
      const returnUrl = currentPath || feedUrl()
      setLastPath(returnUrl)
      navigate(loginUrl(), { replace: true, state: { returnUrl } })
    },
    [setLastPath]
  )

  return {
    setLastPath,
    getLastPath,
    clearLastPath,
    redirectToLogin,
  }
}

export default useLastPath
